import { useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { set } from "lodash";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults builders
import { Adjustment, ReputationAdjustment, VolumeBonusTier, VolumeBonusesTier } from "@/PayPlans/helpers/DefaultSections";

// Utils
import { getPositionInputDefinitions, getPositionInputDelta } from "./utils";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";
import ReputationTiers from "@/PayPlans/components/Commons/ReputationTiers";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Tier Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";

// MUI
import { Grid, Stack, Collapse, Divider } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const minimumColumns = 1;
const maximumColumns = 7;
const maximumTiers = Number.MAX_VALUE;

const minimumGrossInput = 1;
const maximumGrossInput = 100000000;

const minimumUnitsInput = 1;
const maximumUnitsInput = 10000;

const minimumPositiveReviewInput = 1;
const maximumPositiveReviewInput = 500;

const subjectToReputationLabel = "Subject to Reputation Management";

const lowerColumns = {
	_id: "adjustments",
	actions: [{ label: subjectToReputationLabel, on: "subjectToReputation" }],
	left: { _id: "reviews", title: "Positive Reviews", useValues: ["from", "to"] },
	right: { _id: "adjustment", title: "Adjustment", useValues: ["adjustment"] },
};

const checkMatrixLengthError = (args) => {
	const { make, tiers, componentErrors } = args;
	const errors = [];

	const columns = tiers?.[0]?.bonuses?.length;
	const rows = tiers?.length;

	componentErrors.current[make] = rows <= 1 && columns <= 1;

	if (Object.values(componentErrors.current).every((item) => item)) {
		errors.push("Please add at least a two tiers configuration");
	}

	return errors;
};

const updateValueColumn = (args) => {
	const { currentTiers, column, updatedValue, area, position } = args;

	const delta = getPositionInputDelta(position);

	currentTiers.forEach((tier) => {
		tier.bonuses?.forEach((bonus, key) => {
			if (area in bonus && key === column) {
				bonus[area]["value"] = updatedValue;
				if (tier.bonuses?.[key + 1]) {
					tier.bonuses[key + 1]["from"]["value"] = updatedValue + delta;
				}
			}
		});
	});
};

const updateValueRow = (args) => {
	const { currentTiers, row, column, updatedValue, area } = args;

	if (area !== "bonus") {
		currentTiers[row][area]["value"] = updatedValue;
		if (currentTiers?.[row + 1] && area === "to") {
			currentTiers[row + 1]["from"]["value"] = updatedValue + 0.01;
		}
	} else {
		currentTiers[row]["bonuses"][column][area]["value"] = updatedValue;
	}
};

const mergeErrorsBonusesValidation = (results = new Map(), section = []) => {
	if (!results?.size) {
		return [];
	}

	const rows = results?.get("ROWS") || [];

	if (section.length === 1 && section[0].length === 1) {
		return [...rows];
	}

	const firstValue = section?.[0]?.[0]?.["bonus"]?.value;

	for (const row of section) {
		if (row.some((cell) => cell?.bonus?.value !== firstValue)) {
			return [...rows];
		}
	}

	return rows.map((row) => {
		return row.map(() => {
			return {
				bonus: true,
			};
		});
	});
};

const getTooltipMakeTitle = (make = "") => {
	const modifiers = {
		"Pre-Owned Vehicles": "Used Vehicles",
	};
	return modifiers[make] || make;
};

const getDisabledReputation = (reputationStatus, make = "") => {
	if (!reputationStatus || !make) {
		return false;
	}
	const { combined = false, sections = [] } = reputationStatus || {};

	if (make === "Combined") {
		if (!sections?.length) {
			return false;
		}
		return sections.some((item) => Object.values(item)[0]);
	}

	return combined;
};

export default function VolumeBonus(props) {
	const {
		make = "",
		tiers = [],
		position = "",
		readOnly = false,
		subjectToReputation = false,
		forgiveness = null,
		adjustments = [],
		componentErrors = {
			current: {},
		},
		reputationStatuses = {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();
	const lowerErrorsMessages = getSectionsErrorMessages(["reviews", "adjustment"]);
	const localErrorsMessages = getSectionsErrorMessages(["gross", "units", "bonuses"]);

	const headerErrors = errors?.get("units");
	const rowsErrors = errors?.get("gross");
	const columnsErrors = errors?.get("bonuses");

	// Control to send update actions
	const wasUpdated = useRef(false);
	const wasLowerUpdated = useRef(false);
	const {
		reputationStatus = {
			sections: [],
			combined: false,
		},
		setReputationStatus = () => {},
	} = reputationStatuses;

	const onUpdateModel = (args) => {
		const validToSave = isValid && !matrixLengthError?.length && Object.values(componentErrors.current).some((item) => !item);
		onActionValidation?.(make, validToSave);

		const matrixValid = !matrixLengthError?.length && Object.values(componentErrors.current).some((item) => !item);
		onActionValidation?.("component", matrixValid);

		if (validToSave && (wasUpdated.current || wasLowerUpdated.current)) {
			const { updatedTiers, updatedLowerTiers } = args;
			const updatedModel = { tiers: updatedTiers, lowerTiers: updatedLowerTiers };
			onActionUpdate?.(make, updatedModel);
		}
	};

	const onTiersUpdate = (updatedTiers) => {
		onUpdateModel({ updatedTiers, updatedLowerTiers: lowerTiers });
	};

	const onLowerUpdate = (updatedTiers) => {
		onUpdateModel({ updatedTiers: innerTiers, updatedLowerTiers: updatedTiers });
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);
	const [lowerTiers, setLowerTiers] = useCallbackState({}, onLowerUpdate);

	const matrixLengthError = checkMatrixLengthError({
		make,
		componentErrors,
		tiers: innerTiers,
	});

	// Methods
	const handleAddColumn = () => {
		const currentTiers = [...innerTiers];
		const bonusInput = currentTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = currentTiers?.[0]?.["bonuses"]?.length ? currentTiers[0]["bonuses"].length - 1 : 0;
		const delta = position === "Sales Associate" ? 0.5 : 1;

		currentTiers.forEach((tier) => {
			tier.bonuses.push(VolumeBonusTier({ from: minimumUnitsInput + delta, to: maximumUnitsInput, bonus: 0, bonusInput }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = minimumUnitsInput;
			}
		});

		wasUpdated.current = false;
		setInnerTiers(clone(currentTiers));
	};

	const handleDeleteColumn = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			currentTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumUnitsInput;
			});
		}
		const updatedTiers = checkIsOnlySingleValue(currentTiers);
		update(clone(updatedTiers));
	};

	const handleAddTier = () => {
		const currentTiers = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const bonusInput = currentTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const previousBonusesLength = currentTiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		for (let i = 0; i < previousBonusesLength; i++) {
			const previousFrom = currentTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = currentTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] || 0;
			bonuses[i] = VolumeBonusTier({ from: previousFrom, to: previousTo, bonus: 0, bonusInput });
		}

		currentTiers.push(VolumeBonusesTier(minimumGrossInput + 0.01, maximumGrossInput, bonuses));

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = minimumGrossInput;
		}

		onActionValidation?.(make, false);
		wasUpdated.current = false;
		setInnerTiers(clone(currentTiers));
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		currentTiers[lastIndex].to.value = maximumGrossInput;
		const updatedTiers = checkIsOnlySingleValue(currentTiers);
		update(clone(updatedTiers));
	};

	const checkIsOnlySingleValue = (currentTiers) => {
		if (currentTiers?.length === 1 && currentTiers[0]?.["bonuses"]?.length === 1) {
			currentTiers[0]["bonuses"][0]["bonus"]["value"] = 0;
			componentErrors.current[make] = true;
		}
		return currentTiers;
	};

	const handleUpdateInput = (...params) => {
		const [, , location, area, input] = params;
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(currentTiers);
	};

	const handleUpdateValue = (...params) => {
		const [row, column, location, area, value] = params;
		const currentTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updateValueColumn({ currentTiers, column, updatedValue, area, position });
				break;
			case "row":
				updateValueRow({ currentTiers, row, column, updatedValue, area });
				break;
			default:
				break;
		}

		update(currentTiers);
	};

	const update = (updatedTiers = []) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const updateLower = (updatedTiers = {}) => {
		wasLowerUpdated.current = true;
		setLowerTiers(updatedTiers);
	};

	const handleSwitchChange = (value) => {
		wasLowerUpdated.current = value;

		if (make === "Combined") {
			setReputationStatus({ ...reputationStatus, combined: value });
		} else {
			const prevValues = [...(reputationStatus.sections ?? [])];
			const index = prevValues.findIndex((item) => Object.keys(item)[0] === make);
			if (index === -1) {
				prevValues.push({ [make]: value });
			} else {
				prevValues[index][make] = value;
			}
			setReputationStatus({ ...reputationStatus, sections: [...prevValues] });
		}

		updateLower({
			subjectToReputation: value,
			forgiveness: {
				input: "Unit",
				value: 0,
			},
			adjustments: value ? ReputationAdjustment() : [],
		});
	};

	const handleLowerAddTier = () => {
		const updated = { ...lowerTiers };
		const lastIndex = updated?.adjustments?.length;

		updated?.adjustments.push(Adjustment(minimumPositiveReviewInput, maximumPositiveReviewInput));
		if (lastIndex) {
			updated.adjustments[lastIndex - 1].to.value = 0;
		}

		wasLowerUpdated.current = false;
		updateLower(updated);
	};

	const handleLowerDeleteTier = () => {
		const updated = { ...lowerTiers };

		if (updated?.adjustments?.length > 2) {
			updated.adjustments.splice(-1);
			const lastIndex = updated.adjustments.length;
			updated.adjustments[lastIndex - 1].to.value = 100;
		}

		updateLower(updated);
	};

	const handleLowerUpdateValue = ({ row, area, value, delta = 0.01 }) => {
		const updated = { ...lowerTiers };
		updated.adjustments[row][area]["value"] = value ? Number(value) : 0;
		if (updated?.adjustments?.[row + 1] && area === "to") {
			updated.adjustments[row + 1]["from"]["value"] = (value ? Number(value) : 0) + delta;
		}
		updateLower(updated);
	};

	const handleForgiveness = (key, value) => {
		const updated = { ...lowerTiers };
		if (key === "input") {
			set(updated, `forgiveness.value`, 0);
		}
		set(updated, `forgiveness.${key}`, value);
		updateLower(updated);
	};

	const mergeErrorsUnitsValidation = (results = new Map(), section = []) => {
		return section.map((row, key) => {
			const stepToCheck = position === "Sales Associate" ? 0.5 : 1;
			return {
				from: results?.[key]?.from || row?.from?.value % stepToCheck !== 0,
				to: results?.[key]?.to || row?.to?.value % stepToCheck !== 0,
			};
		});
	};

	useMemo(() => {
		if (lowerTiers?.adjustments) {
			validate([
				{
					definition: {
						useKey: "reviews",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: lowerTiers?.adjustments || [],
				},
				{
					definition: {
						useKey: "adjustment",
						operators: {
							conditions: [
								{
									type: "STEP",
									on: "value",
									compare: "<", // If current (upper) < next is good
									deltas: { Percent: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["adjustment"],
						message: "Invalid adjustment definition",
						validationType: "SIMPLE",
					},
					section: lowerTiers.adjustments || [],
				},
			]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lowerTiers, lowerTiers.adjustments, validate]);

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "gross",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: ">=",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
									distinct: 1,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "units",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 1 }, // Add delta on upper value
									distinct: 0,
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsUnitsValidation,
							},
						},
						errorsMap: ["from", "to"],
						message: "Units invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper > lower is good,
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsBonusesValidation,
							},
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
			]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [innerTiers, validate]);

	useEffect(() => {
		setLowerTiers({
			subjectToReputation,
			forgiveness,
			adjustments: clone(adjustments) || [],
		});
		return () => {
			wasLowerUpdated.current = false;
			setLowerTiers({});
		};
	}, [subjectToReputation, forgiveness, adjustments, setLowerTiers]);

	useEffect(() => {
		setInnerTiers(clone(tiers));
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	const isSubjectToReputationDisabled = getDisabledReputation(reputationStatus, make);

	return (
		<>
			<Grid container item mt={2} xs={12}>
				<Grid item xs={12}>
					<TierColumns
						titleLabel={"Units"}
						titleContainerProps={{
							justifyContent: "flex-start",
							alignItems: "end",
							sx: { maxWidth: "241px", width: "241px", marginRight: "3px" },
						}}
						titleProps={{
							className: "tiered-title tiered-title-adjustment",
							sx: null,
						}}
						disabledDelete={innerTiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
						disabledAdd={innerTiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
						onClickDelete={() => handleDeleteColumn()}
						onClickAdd={() => handleAddColumn()}
					>
						<Stack direction={"row"} position={"relative"}>
							{innerTiers?.[0]?.bonuses?.map((tier, key) => {
								const indexKey = `volume_bonus_column_${key}`;
								const fromDisabled = Boolean(!key);
								const toDisabled = Boolean(innerTiers?.[0]?.bonuses.length === key + 1);

								return (
									<TierColumnsItem key={indexKey} index={key} title={"Bonus"}>
										<MatrixColumn
											index={key}
											item={tier}
											position={position}
											errors={headerErrors}
											fromDisabled={fromDisabled}
											toDisabled={toDisabled}
											readOnly={readOnly}
											onActionUpdate={handleUpdateValue}
										/>
									</TierColumnsItem>
								);
							})}
						</Stack>
					</TierColumns>
				</Grid>

				<Grid item xs={12} mt={1} minHeight={"100px"}>
					<TierRows
						showTitle
						title={"Gross ($)"}
						sxTitlePropsContainer={{
							top: 102,
							left: 10,
						}}
						centerTitleAfterRow={1}
						deleteButtonText={"Remove Tier"}
						addButtonText={"Add Tier"}
						deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
						addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
						spaces={{
							mainSection: 235,
							columns: 101,
							totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
							totalRows: innerTiers?.length || 1,
							separation: 5.5,
						}}
						rowsAreaProps={{
							ml: "35px",
						}}
						onClickDelete={() => handleDeleteTier()}
						onClickAdd={() => handleAddTier()}
					>
						<TransitionGroup>
							<Collapse>
								<Stack direction="column" rowGap={2}>
									{innerTiers?.map((tier, key) => {
										const indexKey = `volume_bonus_row_${key}`;
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										const bonusesLength = innerTiers?.[0]?.bonuses?.length || 0;
										const reduction = bonusesLength * 1.5 + 3 + bonusesLength;

										return (
											<Grid key={indexKey} container item>
												<MatrixRow
													index={key}
													item={tier}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													bonusInputData={getPositionInputDefinitions(position)}
													spaces={{
														mainSection: 235,
														columns: 101,
														totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
														totalRows: innerTiers?.length || 1,
														separation: 5.5,
														reduction,
													}}
													rowsErrors={rowsErrors || []}
													columnsErrors={columnsErrors || []}
													readOnly={readOnly}
													spacing={"9px"}
													sxMainContent={{ minWidth: "170.5px", marginRight: "3px" }}
													sxCells={{ minWidth: "100.42px" }}
													onActionUpdate={handleUpdateValue}
													onChangeType={handleUpdateInput}
												/>
											</Grid>
										);
									})}
								</Stack>
							</Collapse>
						</TransitionGroup>

						{Boolean(localErrorsMessages?.length || matrixLengthError?.length) && (
							<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
								{localErrorsMessages.map((message, key) => {
									const indexKey = `local_error_${key}`;
									return <TierAlertError key={indexKey} text={message} />;
								})}
								{matrixLengthError.map((message, key) => {
									const indexKey = `matrix_error_${key}`;
									return <TierAlertError key={indexKey} text={message} />;
								})}
							</Grid>
						)}
					</TierRows>
				</Grid>
			</Grid>
			<Divider className="!border-disabled" sx={{ margin: "30px 0" }} variant="middle" />
			<ReputationTiers
				readOnly={readOnly}
				rows={lowerTiers?.adjustments}
				content={lowerColumns}
				tooltips={{
					reviews: {
						title: `Ratio of reviews to ${getTooltipMakeTitle(make)} units`,
						placement: "right",
					},
				}}
				showForgiveness
				showActions
				forgiveness={lowerTiers?.forgiveness}
				showContent={lowerTiers?.subjectToReputation}
				actionsJustifyContent={"flex-end"}
				sxContent={{ marginLeft: "35px" }}
				actions={[
					<CustomizedSwitches
						key={`bonus-allowance-${make}-social-media`}
						disabled={readOnly || isSubjectToReputationDisabled}
						textChecked={subjectToReputationLabel}
						textUnChecked={subjectToReputationLabel}
						checked={lowerTiers?.subjectToReputation}
						onChange={handleSwitchChange}
						tooltip={{
							title: "Dependency on reputation management.",
							placement: "right",
							key: "bonus-allowance-social-media-tooltip",
						}}
					/>,
				]}
				errors={errors}
				errorsMessages={lowerErrorsMessages}
				onActionUpdate={(positions, area, value) => {
					if (positions) {
						return handleLowerUpdateValue({
							row: positions.row,
							delta: 0.01,
							area,
							value,
						});
					}
					return handleForgiveness(area, value);
				}}
				onActionAdd={handleLowerAddTier}
				onActionDelete={handleLowerDeleteTier}
				maxDoubleInputValue={1000}
			/>
		</>
	);
}
VolumeBonus.propTypes = {
	make: PropTypes.string,
	tiers: PropTypes.array,
	position: PropTypes.string,
	readOnly: PropTypes.bool,
	subjectToReputation: PropTypes.bool,
	forgiveness: PropTypes.object,
	adjustments: PropTypes.array,
	reputationStatuses: PropTypes.object,
	componentErrors: PropTypes.shape({
		current: PropTypes.object,
	}),
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
